// "/": the landing page for would-be partners. Signed in, the cabinet's dashboard instead.

import type { PageContext } from '../router.ts';
import { isAuthenticated } from '../auth.ts';
import { languageSelector } from '../components/language-selector.ts';
import { h, svg } from '../dom.ts';
import { t } from '../i18n.ts';
import { track } from '../metrics.ts';
import { state } from '../state.ts';

const K = 'influencer.web.landing';

const loginIcon = () => svg(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" fill="currentColor" aria-hidden="true"><path d="M11 7 9.6 8.4l2.6 2.6H2v2h10.2l-2.6 2.6L11 17l5-5-5-5zm9 12h-8v2h8c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2h-8v2h8v14z"/></svg>`);
const menuIcon = () => svg(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" fill="currentColor" aria-hidden="true"><path d="M3 18h18v-2H3v2zm0-5h18v-2H3v2zm0-7v2h18V6H3z"/></svg>`);
const closeIcon = () => svg(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" fill="currentColor" aria-hidden="true"><path d="M19 6.41 17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg>`);
const chevron = (left: boolean): Element => svg(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" fill="currentColor" aria-hidden="true"><path d="${left
    ? 'M15.41 7.41 14 6l-6 6 6 6 1.41-1.41L10.83 12z'
    : 'M10 6 8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z'}"/></svg>`);
const expandIcon = () => svg(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" fill="currentColor" aria-hidden="true"><path d="M16.59 8.59 12 13.17 7.41 8.59 6 10l6 6 6-6z"/></svg>`);

export function home(ctx: PageContext, actions: { onLanguage: (name: string) => void }): void {
    const s = { headerExpanded: false, openQuestions: new Set<number>() };

    ctx.title(`${K}.partnerTitle`, 'Lify Influencer');
    track('influencerHomeViewed');

    function goToDashboard(): void {
        track('influencerLoginCtaClicked');
        ctx.go('/dashboard');
    }

    if (isAuthenticated()) {
        goToDashboard();

        return;
    }

    const sections = [['about', 'aboutLify'], ['differency', 'whatMakesDifferent'], ['benefits', 'benefits'], ['faq', 'faq']];

    const links = (close: boolean): HTMLElement[] => sections.map(([id, key]) => h('div', { class: 'link' },
        h('a', { href: `#${id}`, onclick: close ? () => {
            s.headerExpanded = false;
            ctx.rerender();
        } : null }, t(`${K}.header.links.${key}`))));

    const slide = (direction: number): void => {
        const slider = document.getElementById('slider');

        if (!slider) return;

        const next = Math.max(0, Math.min(slider.scrollWidth, slider.scrollLeft + direction * 424));

        slider.scrollTo({ behavior: 'smooth', left: next, top: 0 });
    };

    const textBlocks = (prefix: string, count: number, titleKey: string, bodyKey: string, numberKey?: string): HTMLElement[] => Array.from({ length: count }, (_, i) => h('div', { class: 'block' },
        numberKey && h('div', { class: 'block-number' }, t(`${prefix}.block${i + 1}.${numberKey}`)),
        h('div', { class: 'block-title' }, t(`${prefix}.block${i + 1}.${titleKey}`)),
        h('div', { class: 'block-content' }, t(`${prefix}.block${i + 1}.${bodyKey}`))));

    function view() {
        return h('div', { class: 'page-home' },
            h('div', { class: 'background' },
                h('div', { class: 'gradient-left-circle' }),
                h('div', { class: 'gradient-right-circle' }),
                h('div', { class: 'content' },
                    h('div', { class: 'header' },
                        h('div', { class: 'upper-header' },
                            h('img', { class: 'logo', src: '/images/landing_header_logo.svg', alt: 'Lify' }),
                            h('div', { class: 'sections' }, links(false)),
                            h('div', { class: 'buttons' },
                                languageSelector({
                                    value: state.locale,
                                    locales: state.locales,
                                    label: t('influencer.web.layout.header.language.placeholder'),
                                    onChange: name => actions.onLanguage(name)
                                }),
                                h('button', { type: 'button', class: 'btn primary-button text-login-button', onclick: goToDashboard },
                                    t(`${K}.buttons.login`)),
                                h('button', {
                                    type: 'button',
                                    class: 'btn icon-login-button primary-button',
                                    'aria-label': t(`${K}.buttons.login`),
                                    onclick: goToDashboard
                                }, loginIcon()),
                                h('button', {
                                    type: 'button',
                                    class: 'btn mobile-header-expandable-toggle',
                                    'aria-expanded': s.headerExpanded ? 'true' : 'false',
                                    'aria-label': 'Menu',
                                    onclick: () => {
                                        s.headerExpanded = !s.headerExpanded;
                                        ctx.rerender();
                                    }
                                }, s.headerExpanded ? closeIcon() : menuIcon())),
                            h('div', { class: `expandable-header ${s.headerExpanded ? 'expanded' : 'collapsed'}` },
                                h('div', { class: 'sections' }, links(true))))),

                    h('img', { class: 'man section', src: '/images/man.webp', alt: '', width: 675, height: 357 }),
                    h('div', { class: 'big-title section' }, t(`${K}.partnerTitle`)),
                    h('div', { class: 'subtitle' }, t(`${K}.partnerSubtitle`)),
                    h('div', { class: 'buttons' },
                        h('button', { type: 'button', class: 'btn primary-button', onclick: goToDashboard }, t(`${K}.buttons.login`)),
                        h('a', { class: 'btn secondary-button', href: '#about' }, t(`${K}.buttons.discoverPhilosophy`))),

                    h('div', { class: 'section title', id: 'about' }, t(`${K}.philosophyTitle`)),
                    h('div', { class: 'subtitle' }, t(`${K}.philosophySubtitle`)),
                    h('div', { class: 'girl-wrapper section' },
                        h('div', { class: 'blocks' },
                            h('img', { class: 'girl', src: '/images/girl.webp', alt: '', loading: 'lazy' }),
                            h('div', { class: 'blocks-row' }, textBlocks(`${K}.girl.blocks`, 3, 'title', 'content'))),
                        h('div', { class: 'promise' }, t(`${K}.girl.promise.text`)),
                        h('div', { class: 'subtitle' }, t(`${K}.promise.label`))),

                    h('div', { class: 'section title', id: 'differency' }, t(`${K}.appsTitle`)),
                    h('div', { class: 'subtitle' }, t(`${K}.appsSubtitle`)),
                    h('div', { class: 'apps section' },
                        h('div', { class: 'block' },
                            h('div', { class: 'image' },
                                h('div', { class: 'image-title' }, t(`${K}.appsBlock1.image.title`)),
                                h('img', { src: '/images/phone.svg', alt: '', loading: 'lazy', width: 280, height: 374 })),
                            h('div', { class: 'block-title' }, t(`${K}.appsBlock1.title`)),
                            [1, 2, 3, 4].map(i => h('div', { class: 'block-information' }, t(`${K}.appsBlock1.information${i}`)))),
                        h('div', { class: 'block' },
                            h('div', { class: 'image' },
                                h('div', { class: 'image-title' }, t(`${K}.appsBlock2.image.title`)),
                                h('div', { class: 'diagram' },
                                    h('div', { class: 'body flying-text' }, t(`${K}.appsBlock2.image.body.label`)),
                                    h('div', { class: 'flying-text mind' }, t(`${K}.appsBlock2.image.mind.label`)),
                                    h('div', { class: 'energy flying-text' }, t(`${K}.appsBlock2.image.energy.label`)),
                                    h('img', { src: '/images/diagram.svg', alt: '', loading: 'lazy', width: 304, height: 286 }))),
                            h('div', { class: 'block-title' }, t(`${K}.appsBlock2.title`)),
                            [1, 2, 3, 4].map(i => h('div', { class: 'block-information' }, t(`${K}.appsBlock2.information${i}`))))),

                    h('div', { class: 'section sells-title title' },
                        t(`${K}.sellsTitle`),
                        h('div', { class: 'buttons' },
                            h('button', { type: 'button', class: 'btn icon-button secondary-button', 'aria-label': 'Previous', onclick: () => slide(-1) }, chevron(true)),
                            h('button', { type: 'button', class: 'btn icon-button secondary-button', 'aria-label': 'Next', onclick: () => slide(1) }, chevron(false)))),
                    h('div', { class: 'sliding-accordion', id: 'slider' },
                        [1, 2, 3, 4, 5, 6].map(i => h('div', { class: 'block' },
                            h('img', { src: `/images/sliding_block_image${i}.webp`, alt: '', loading: 'lazy', width: 128, height: 128 }),
                            h('div', { class: 'block-title' }, t(`${K}.sellsBlock${i}.title`)),
                            h('div', { class: 'block-subtitle' }, t(`${K}.sellsBlock${i}.subtitle`))))),

                    h('div', { class: 'gray-background' },
                        h('div', { class: 'section title' }, t(`${K}.otherAppsTitle`)),
                        h('div', { class: 'subtitle' }, t(`${K}.otherAppsSubtitle`)),
                        h('div', { class: 'blocks-row section' }, textBlocks(`${K}.otherApps.blocks`, 3, 'title', 'content', 'number')),
                        h('div', { class: 'main-block section' },
                            h('div', { class: 'title' }, t(`${K}.coreFeaturesTitle`)),
                            [1, 2, 3, 4, 5, 6].map(i => h('div', { class: 'sub-block' },
                                h('div', { class: 'sub-block-title' }, t(`${K}.coreFeatures.blocks.block${i}.title`)),
                                h('div', { class: 'sub-block-content' }, t(`${K}.coreFeatures.blocks.block${i}.content`))))),
                        h('div', { class: 'section title' }, t(`${K}.journeyTitle`)),
                        h('div', { class: 'subtitle' }, t(`${K}.journeySubtitle`)),
                        [1, 2, 3].map(i => h('div', { class: 'section steps-block' },
                            h('div', { class: 'numeric-container' }, h('div', { class: 'number' }, t(`${K}.steps.block${i}.number`))),
                            h('div', { class: 'steps-block-content' },
                                h('div', { class: 'steps-block-title' }, t(`${K}.steps.block${i}.title`)),
                                h('div', { class: 'steps-block-subtitle' }, t(`${K}.steps.block${i}.subtitle`)))))),

                    h('div', { class: 'section title', id: 'benefits' }, t(`${K}.rewardTitle`)),
                    h('div', { class: 'subtitle' }, t(`${K}.rewardSubtitle`)),
                    h('div', { class: 'benefits-blocks section' },
                        [['commission', 'commissionBlock'], ['present', 'performanceBlock'], ['tracking', 'trackingBlock'], ['stars', 'payoutsBlock']]
                            .map(([icon, key]) => h('div', { class: 'block' },
                                h('img', { src: `/images/${icon}_icon.svg`, alt: '' }),
                                h('div', { class: 'benefits-block-title' }, t(`${K}.${key}.title`)),
                                h('div', { class: 'benefits-block-subtitle' }, t(`${K}.${key}.subtitle`))))),

                    h('div', { class: 'section title', id: 'faq' }, t(`${K}.faqTitle`)),
                    h('div', { class: 'faq-section' },
                        [1, 2, 3, 4, 5, 6].map(i => {
                            const open = s.openQuestions.has(i);
                            const answerId = `faq-answer-${i}`;

                            // Toggled in place, not redrawn, so the answer slides open as MudExpansionPanel's did.
                            const toggle = (event: MouseEvent): void => {
                                const button = event.currentTarget as HTMLElement;
                                const panel = button.closest('.question')!;
                                const nowOpen = panel.classList.toggle('open');

                                button.setAttribute('aria-expanded', String(nowOpen));

                                if (nowOpen) s.openQuestions.add(i);
                                else s.openQuestions.delete(i);
                            };

                            return h('div', { class: `question${open ? ' open' : ''}` },
                                h('button', { type: 'button', class: 'question-header', 'aria-expanded': String(open), 'aria-controls': answerId, onclick: toggle },
                                    h('span', { class: 'question-title' }, t(`${K}.question${i}.title`)),
                                    h('span', { class: 'question-icon' }, expandIcon())),
                                h('div', { class: 'question-collapse', id: answerId },
                                    h('div', null, h('div', { class: 'question-answer' }, t(`${K}.question${i}.answer`)))));
                        })),

                    h('div', { class: 'footer' },
                        h('div', { class: 'contact-info' },
                            h('div', { class: 'label' }, t(`${K}.footer.email.label`)),
                            h('div', { class: 'email' },
                                h('a', { href: `mailto:${t(`${K}.footer.email.value`)}` }, t(`${K}.footer.email.value`)))),
                        h('div', { class: 'social-media' },
                            ['youTube', 'instagram', 'facebook', 'linkedIn'].map(network => h('div', { class: 'item' },
                                h('a', { href: t(`${K}.footer.socialMedias.${network}.value`), target: '_blank', rel: 'noopener noreferrer' },
                                    t(`${K}.footer.socialMedias.${network}.label`)))))))));
    }

    ctx.render(view);
}
